import { ApiErrorPresenterService } from 'src/app/core/services/api-error-presenter.service';
import { inject } from '@angular/core';

import {
  injectMutation,
  injectQuery,
} from '@tanstack/angular-query-experimental';

import { firstValueFrom } from 'rxjs';

import { TranslateService } from '@ngx-translate/core';

import {
  CompanyApiService,
  CompanyContentPlatform,
} from '../services/company-api.service';
import { isUserOffboardRequired } from './user.queries';

/**
 * Create (PUT /company/create) or update (POST /company/update) a company. The
 * payload is the already-snake_cased, nested company-settings object; the caller
 * builds it and says which of the two it is - the backend guards them differently.
 *
 * No query invalidation - the companies list is a server-side ag-grid rather than
 * a query, and the editor navigates away on success.
 */
export function injectManageCompanyMutation() {
  const companyApi = inject(CompanyApiService);

  return injectMutation(() => ({
    mutationFn: ({
      settingsInSnakeCase,
      isCreate,
    }: {
      settingsInSnakeCase: any;
      isCreate: boolean;
    }) =>
      firstValueFrom(
        isCreate
          ? companyApi.createCompany(settingsInSnakeCase)
          : companyApi.updateCompany(settingsInSnakeCase),
      ),
  }));
}

/**
 * Enable/disable a user (POST /company/user_toggle). The endpoint takes only the
 * user id and flips the flag server-side.
 */
export function injectToggleUserStatusMutation() {
  const companyApi = inject(CompanyApiService);
  const presenter = inject(ApiErrorPresenterService);
  const translate = inject(TranslateService);

  return injectMutation(() => ({
    mutationFn: (userId: string) =>
      firstValueFrom(companyApi.toggleUserStatus(userId)),
    // the owns-objects conflict is not a failure: the caller opens the offboard dialog
    onError: (error: unknown) => {
      if (!isUserOffboardRequired(error)) {
        presenter.present(error, {
          fallback: translate.instant('admin-module.msg.user-update-failed'),
        });
      }
    },
  }));
}

/**
 * GET /company/get_available_content_platforms (SEN-6208 slice 1c): the feed builder's platform picker, served and
 * gated by the backend for the active company. Nomenclature-backed, so a long window; a company switch re-reads it
 * through the key.
 */
export function injectCompanyContentPlatformsQuery() {
  const companyApi = inject(CompanyApiService);

  return injectQuery(() => ({
    queryKey: ['company', 'content-platforms'] as const,
    queryFn: (): Promise<CompanyContentPlatform[]> =>
      firstValueFrom(companyApi.getCompanyAvailableContentPlatforms()),
    staleTime: 10 * 60 * 1000,
  }));
}
